import type {
	ChartDataConfig,
	DimensionConfig,
	MetricConfig,
	SourceConfig,
	TimeDimensionConfig,
} from '../models/chart-config.model';

/**
 * AI sometimes emits bare cube member ids (`websiteClicks`), informal aliases
 * (`phoneCalls`, `clicks`, `location`), title-case labels (`Website Clicks`),
 * or wrong keys (`field` / `title` instead of `name` / `label`). Preview
 * `getCardData` needs fully-qualified schema member names.
 *
 * Source-scoped aliases live in {@link AI_FIELD_MEMBER_ALIASES_BY_SOURCE} so
 * each connector can grow its own map without polluting others.
 */
const AI_FIELD_MEMBER_ALIASES_BY_SOURCE: Record<
	string,
	Record<string, string>
> = {
	googlebusinessprofile: {
		phonecalls: 'callClicks',
		calls: 'callClicks',
		callclicks: 'callClicks',
		/** AI often says "clicks" for website clicks */
		clicks: 'websiteClicks',
		websiteclicks: 'websiteClicks',
		location: 'locationName',
	},
};

function resolveDataSource(
	sources: SourceConfig[] | undefined
): string | undefined {
	const raw = sources?.find(s => typeof s?.dataSource === 'string')
		?.dataSource;
	const trimmed = raw?.trim();
	return trimmed || undefined;
}

function compactMemberKey(member: string): string {
	return member.toLowerCase().replace(/[\s_-]+/g, '');
}

/** `Website Clicks` → `websiteClicks`; leave camelCase ids alone. */
function toCamelMember(member: string): string {
	const trimmed = member.trim();
	if (!trimmed || !/[\s_]/.test(trimmed)) {
		return trimmed;
	}
	const parts = trimmed.split(/[\s_]+/).filter(Boolean);
	if (parts.length === 0) {
		return trimmed;
	}
	return (
		parts[0].toLowerCase() +
		parts
			.slice(1)
			.map(p => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
			.join('')
	);
}

function aliasMember(member: string, cube: string | undefined): string {
	const sourceKey = cube?.toLowerCase();
	const resolve = (token: string): string | undefined => {
		if (sourceKey == null) {
			return undefined;
		}
		return AI_FIELD_MEMBER_ALIASES_BY_SOURCE[sourceKey]?.[
			compactMemberKey(token)
		];
	};

	const direct = resolve(member);
	if (direct) {
		return direct;
	}
	const camel = toCamelMember(member);
	return resolve(camel) ?? camel;
}

function qualifyFieldName(
	name: string | undefined,
	dataSource: string | undefined
): string | undefined {
	const trimmed = name?.trim();
	if (!trimmed) {
		return name;
	}

	const dot = trimmed.indexOf('.');
	if (dot >= 0) {
		const cube = trimmed.slice(0, dot);
		const member = trimmed.slice(dot + 1);
		const aliased = aliasMember(member, cube);
		if (aliased === member) {
			return trimmed;
		}
		return `${cube}.${aliased}`;
	}

	if (!dataSource) {
		return trimmed;
	}
	const aliased = aliasMember(trimmed, dataSource);
	return `${dataSource}.${aliased}`;
}

/**
 * AI tools sometimes emit `field`/`title` instead of `name`/`label`.
 * Map those before qualification so Settings + getCardData see real members.
 */
function normalizeAiNamedField<T extends MetricConfig | DimensionConfig>(
	field: T
): T {
	const record = field as T & {
		field?: unknown;
		title?: unknown;
		name?: unknown;
		label?: unknown;
	};
	const nameFromField =
		typeof record.name === 'string' && record.name.trim()
			? record.name
			: typeof record.field === 'string'
				? record.field
				: undefined;
	const labelFromTitle =
		typeof record.label === 'string' && record.label.trim()
			? record.label
			: typeof record.title === 'string'
				? record.title
				: undefined;

	if (nameFromField === record.name && labelFromTitle === record.label) {
		return field;
	}

	const { field: _dropField, title: _dropTitle, ...rest } = record as T & {
		field?: unknown;
		title?: unknown;
	};
	return {
		...rest,
		...(nameFromField !== undefined ? { name: nameFromField } : {}),
		...(labelFromTitle !== undefined ? { label: labelFromTitle } : {}),
	} as T;
}

function qualifyNamedFields<T extends MetricConfig | DimensionConfig>(
	fields: T[] | undefined,
	dataSource: string | undefined
): T[] | undefined {
	if (!Array.isArray(fields)) {
		return fields;
	}
	return fields.map(field => {
		const normalized = normalizeAiNamedField(field);
		const nextName = qualifyFieldName(normalized?.name, dataSource);
		if (!nextName || nextName === normalized.name) {
			return normalized === field ? field : normalized;
		}
		return { ...normalized, name: nextName };
	});
}

function qualifyTimeDimension(
	timeDimension: TimeDimensionConfig | undefined,
	dataSource: string | undefined
): TimeDimensionConfig | undefined {
	if (!timeDimension || typeof timeDimension !== 'object') {
		return timeDimension;
	}
	const nextDimension = qualifyFieldName(
		timeDimension.dimension,
		dataSource
	);
	if (!nextDimension || nextDimension === timeDimension.dimension) {
		return timeDimension;
	}
	return { ...timeDimension, dimension: nextDimension };
}

/**
 * Qualify short AI metric/dimension/time-dimension names using the card
 * `sources` dataSource.
 */
export function qualifyAiCardDataFields(
	data: ChartDataConfig | Record<string, unknown> | undefined,
	sources: SourceConfig[] | undefined
): ChartDataConfig | Record<string, unknown> | undefined {
	if (!data || typeof data !== 'object' || Array.isArray(data)) {
		return data;
	}
	const dataSource = resolveDataSource(sources);
	const chartData = data as ChartDataConfig;
	const metrics = qualifyNamedFields(chartData.metrics, dataSource);
	const dimensions = qualifyNamedFields(chartData.dimensions, dataSource);
	const timeDimension = qualifyTimeDimension(
		chartData.timeDimension,
		dataSource
	);
	if (
		metrics === chartData.metrics &&
		dimensions === chartData.dimensions &&
		timeDimension === chartData.timeDimension
	) {
		return data;
	}
	return {
		...data,
		...(metrics !== undefined ? { metrics } : {}),
		...(dimensions !== undefined ? { dimensions } : {}),
		...(timeDimension !== undefined ? { timeDimension } : {}),
	};
}
